import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { solidWhenReducedTransparency } from '../styles/reducedTransparency';
import { DYNAMITE_AGENT_ID, DYNAMITE_CAPABILITY_URL, DYNAMITE_PROVIDER, DYNAMITE_RETENTION_DAYS } from '../config/dynamite';
import { getStoredConsent, CONSENT_CHANGED_EVENT } from '../services/analyticsConsent';
import { CONTACT_PHONE } from '../config/servicesConfig';
import DynamiteOrb, { DynamiteCore, orbCoreCenter, ORB_SIZE, ORB_SIZE_PHONE } from './DynamiteOrb';
import { resolveDynamiteLauncher } from '../utils/dynamiteLauncher';
import avatarSyncManager from '../services/AvatarSyncManager';

/**
 * The launcher IS the avatar canvas (DWEB-131, v6.5.1) — the robot head, or
 * the demo's Regina / Roger when the interactive demo syncs one in. It is
 * lazy because it brings three.js with it: at 83507c0 the chatbot imported
 * it eagerly and three.js sat in main.js; v6.5.0 measured main.js at
 * 1.00 MB with three.js only in the lazy demo/logo chunks, and this keeps
 * it there. The fallback below is the v6.5.0 image button at the canvas's
 * size, so the hero CTA and the DynaMite card can open the chat before the
 * chunk lands (both go through openDynaMite / the event, not the launcher).
 */
const DynamiteAvatar = lazy(() => import('./DynamiteAvatar'));

/**
 * DWEB-193 §2: the markdown renderer for agent replies is its own chunk
 * (AgentMarkdown.js). react-markdown and its unified / micromark stack were
 * 157,716 bytes of main.js, parsed by every visitor on every route before
 * first paint, although nothing renders markdown until a reply arrives.
 * `loadAgentMarkdown` is called when the panel first opens, so the chunk is
 * normally in before the greeting; until it lands a reply shows as plain text.
 */
const loadAgentMarkdown = () => import('./AgentMarkdown');
const AgentMarkdown = lazy(loadAgentMarkdown);

/**
 * THE LAUNCHER WEARS TWO FACES (DWEB-144 v6.6.0, DWEB-146 v6.6.1).
 *
 * DYNAMITE_LAUNCHER (config/dynamite.js) is the DEFAULT face: 'orb' in
 * production — CSS only, still at rest, no canvas, nothing from three.js
 * fetched — or 'avatar', the v6.5.1 robot head. The constant stays, and the
 * suite's second project still exercises the other default statically.
 *
 * The interactive demo's "Sync with DynaMite Chatbot" toggle OVERRIDES it at
 * runtime, per page load, through AvatarSyncManager exactly as v6.5.1 did:
 * sync on -> the launcher is the chosen GLB head with the badge
 * "DynaMite (regina)"; sync off -> the default face returns. The owner's
 * decision, verbatim: "rather than the glb avatars be in the chatbot header,
 * the orb functionality can stay there. only the orb launcher that was
 * dynamite avatar before can switch like before into avatar?" So the face is
 * `synced ? 'avatar' : DYNAMITE_LAUNCHER`, subscribed to the manager below,
 * and the panel's header keeps the orb's core as its state indicator
 * whichever face launched it. Nothing about the selection is stored — the
 * privacy policy enumerates every key the site writes (DWEB-143), and this
 * is not one.
 *
 * "Disconnected, not removed": DynamiteAvatar.js, AvatarSyncManager.js and
 * avatarFactory.js stay on disk, stay imported here, and the e2e suite
 * forces the avatar launcher in a project of its own on every run
 * (playwright.config.js). The orb is imported eagerly (top of file): it is
 * a styled button with no dependency, and making it lazy would only put a
 * Suspense fallback in front of a small component.
 */

/**
 * DynaMite — the AI Phone Receptionist, in text (DWEB-121), on the site's
 * own chat surface driving the ElevenLabs JavaScript SDK (v6.5.0).
 *
 * WHY THE SDK AND NOT THE DROP-IN WIDGET. v6.4.8 shipped
 * @elevenlabs/convai-widget-embed: a custom element that rendered the whole
 * chat surface inside a shadow root. This component then had to reach into
 * that shadow root and click a button it did not render to open the panel —
 * the one coupling to the widget's internals — and there was nowhere in that
 * arrangement to put the avatar canvas DWEB-131 restores (v6.5.1). With the
 * SDK the site owns the launcher, the panel, the transcript and the
 * composer; the SDK owns the WebSocket to the agent and nothing visible.
 *
 * WHAT THE SDK IS ASKED FOR. One text-only session
 * (`Conversation.startSession({ textOnly: true })`), which the pinned dist
 * routes over a single WebSocket to api.elevenlabs.io — no WebRTC, no
 * LiveKit, no AudioContext, no worklet fetch. Text-only is set on the AGENT
 * (`conversation.text_only: true`); the client flag repeats it because the
 * SDK picks its transport from it, and a client flag cannot widen what the
 * agent allows. The SDK is imported lazily, so its chunk (which still
 * carries livekit-client for the voice path this site does not take) leaves
 * this origin only when a visitor starts a conversation — and nothing at all
 * is contacted on page load. The embed fetched its configuration from
 * ElevenLabs on every page view; that request is gone.
 *
 * v6.5.2 (DWEB-142) adds one thing the embed never had: a static one-line
 * bubble beside the launcher, shown only once the analytics consent choice
 * is made, dismissible and persisted — see LauncherRow / Callout below.
 *
 * FEATURE PARITY WITH THE EMBED, then the two pieces v6.5.1 adds. A launcher
 * in the bottom-right corner; a panel with the transcript; a notice before
 * the first message (the embed showed the agent's terms text; this one says
 * what the privacy policy says, in the site's words); the agent's greeting
 * from the agent's own `first_message`; a composer; end and close. No
 * transcript persistence — the embed had none. v6.5.1: the launcher is the
 * avatar canvas restored from 83507c0 (DWEB-131), and the panel has the
 * maximize / restore control the embed's `show_resize_button: true` gave it
 * and v6.5.0 dropped (DWEB-141). What v6.5.1 deliberately does NOT restore
 * from the embed's `platform_settings.widget`: `feedback_mode: "during"` and
 * `end_feedback: {type: "rating"}` — UI that collected nothing across the
 * three conversations that exist; the owner decides that on DWEB-141, and
 * the default is no. Two rows of that config were verified rather than
 * assumed: `dismissible: false` — matched, the launcher has no dismiss
 * control and outlives the panel; `markdown_link_allowed_hosts: []` — NOT
 * matched: the embed rendered links as plain text, and this panel renders
 * markdown links in agent replies as live anchors (MarkdownLink in AgentMarkdown.js,
 * target=_blank, react-markdown's default URL transform, any host). That is
 * new behaviour, reported on DWEB-141 as a link-safety decision for the
 * owner, and left as it shipped in v6.5.0 until it is made.
 *
 * WHAT IS DELIBERATELY NOT HERE. The deterministic answer layer
 * (src/services/pricingAnswers.js, complianceAnswers.js) is kept in the tree
 * with its specs but no longer runs in front of any model: the hosted
 * agent's prompt carries the compliance-silence rule and the offers document
 * carries every price verbatim from PRICING. Whether the guarantee should be
 * re-implemented in front of the hosted agent is a design decision recorded
 * on DWEB-121, not something this component decides.
 *
 * DWEB-83 STILL BINDS: the panel schedules no animation frames and runs no
 * keyframe animation; the avatar launcher's render loop is BOUNDED (a short
 * arrival window anchored to the page's own clock, then a nudge on hover,
 * focus, click and when a synced avatar lands — see DynamiteAvatar.js); the
 * orb's pull, the panel's reveal and the travelling core (v6.6.1, below) are
 * CSS transitions driven by event handlers and one layout effect — no
 * requestAnimationFrame anywhere on this surface. The embed's orb was a
 * permanent rAF loop and was caught by mainthread.spec.js; dynamite.spec.js
 * samples the rAF census with the panel open and a session attempted, after
 * the criterion's five-second clock AND after the launcher's nudge window,
 * and expects zero; dynamite-orb.spec.js samples it during the pull, the
 * open, the states and the close.
 *
 * THE ORB BECOMES THE PANEL (DWEB-146). The panel is PRE-SIZED — it mounts
 * at its full box — and is revealed by a `clip-path: circle()` growing from
 * the launcher's center over .52s; the orb's core travels to the panel's
 * header (Fly below, a `left`/`top` transition, .5s) and becomes the state
 * indicator (DynamiteCore). Close reverses the clip and unmounts the panel
 * when the transition ends. NO element with a backdrop-filter changes its
 * box, ever: the panel is clipped, never resized; the orb is never
 * resized. That is the paint-cost risk the prototype was built to avoid, and
 * the spec asserts it across the transition. Both faces open the panel with
 * the same reveal; only the travelling core is orb-specific.
 *
 * THE ORB STAYS IN THE CORNER, EMPTIED (DWEB-150, v6.6.3). Until v6.6.2 the
 * whole orb faded to zero while the panel was open. Now only its CORE layer
 * is hidden — the point of light has moved into the conversation — and the
 * lit sphere stays where it was, at opacity 1, as the close control. On
 * close the core FLIES BACK: the same travelling dot (Fly), from the header
 * core's center to the orb's core, .5s, and the orb's layer reappears when
 * it lands (`coreHidden` on DynamiteOrb: mounted-or-flying). The pull stays
 * off while the panel is open.
 *
 * THE PANEL IS GLASS AGAIN (DWEB-150). The old chat panel (Chatbot.js at
 * 5215e8d) was `blur(25px) saturate(200%)` over a 10% tint; v6.5.0's panel
 * was a 96% fill with a blur behind it that paid DWEB-86's price for
 * nothing visible. The panel now wears the site's recipe — the orb's and
 * the avatar circle's: a strong blur, saturation and a touch of brightness
 * (the lit shell), a LOW tint with a FLOOR, the top-lit border and a depth
 * ramp. The floor is the readability guarantee: over the light section of
 * the page (the partners' white cards, or the system light theme) the
 * glass alone would wash the text out, so the panel keeps a minimum dark
 * alpha (PANEL_TINT_ALPHA) and the surfaces that carry text — the header
 * band, the agent bubble, the notice, the status/composer band — keep
 * their own darker backgrounds. e2e/dynamite-glass.spec.js measures the
 * contrast of the agent bubble, the header and the status line over a
 * pure-white sheet (the light section's upper bound) and over the partners
 * section under the light theme, and requires 4.5:1. The Callout beside
 * the launcher stays solid by its own comment.
 */

/*
 * THE REPLY STREAMS, AND THE CORE FOLLOWS THE TURN (DWEB-147, v6.6.2).
 *
 * Measured on production (v6.6.1, conv_2601m1t9tedvebb8e3yaymg54bh4,
 * 2026-09-06, a WebSocket wrapper on the apex): the agent sends
 * `agent_chat_response_part` events — `start`, then 57 `delta` parts over
 * about a second for a generated answer, then `stop` — BEFORE the
 * `agent_response` that carries the canonical text, and v6.6.1 registered
 * only `onMessage`, so the transcript changed in one mutation and the header
 * core read `idle` throughout. The pinned SDK (@elevenlabs/client 1.24.0,
 * dist/types.d.ts) exposes the parts as `onAgentChatResponsePart`; nothing
 * here is a typewriter over the full message. `start` opens a live agent
 * bubble, each `delta` appends, `stop` closes the turn, and `onMessage`
 * RECONCILES: the bubble ends on the canonical text whatever the deltas did
 * (on production the two agreed to the byte; the reconcile is what makes
 * that a guarantee rather than an observation). `onAgentResponseCorrection`
 * replaces the last agent bubble likewise.
 *
 * The core's state (DYNAMITE_ORB_STATES) is DERIVED from the text turn, no
 * voice needed:
 *   listening  session live, panel open, the composer focused, turn ready
 *   thinking   a message sent, no `start` yet (or the agent says it types)
 *   speaking   between `start` and `stop`
 *   idle       otherwise, and always when the session is not live
 * The same value goes to the orb face's `data-state`, so a turn still in
 * flight shows on the launcher when the panel is closed. SET STATE; NEVER
 * ADD A LOOP — DynamiteOrb.js keys the motion on the attribute and it ends
 * with the state. e2e/dynamite-stream.spec.js plays the agent through the
 * suite's WebSocket route (nothing leaves the machine) and samples the rAF
 * census DURING a streamed reply.
 */

/*
 * VOICE — TEXT STAYS THE DEFAULT, THE MIC IS OPT-IN PER CONVERSATION
 * (DWEB-130, v6.6.3). The owner's four decisions (2026-09-06): text by
 * default, the mic opt-in per conversation; the phone line's voice; audio
 * retained 30 days like the telephone line; a ceiling on the agent. The
 * agent's side of those is the owner's to set and check:agent-kb's to hold
 * (agent-kb/documents.json, the web agent's surfaceConfig).
 *
 * ESTABLISHED FROM THE SDK (@elevenlabs/client 1.24.0, dist/), not from
 * the widget's old config flags:
 *   - `Conversation.startSession` picks TextConversation or
 *     VoiceConversation by `textOnly` and there is NO mode switch on a
 *     session (TextConversation.setMicMuted throws). So text -> voice is
 *     END-AND-RESTART: a second session, a second socket, a second
 *     conversation id. The SDK carries nothing across; this component owns
 *     the transcript, so the bubbles stay on screen, and the agent starts a
 *     fresh conversation (it greets again, in voice).
 *   - The web strategy's FIRST act inside VoiceConversation.startSession is
 *     `getUserMedia({ audio: true })` — the browser's permission prompt —
 *     before any socket. So the explanation card below is shown BEFORE
 *     startSession is called, and a denial rejects startSession before
 *     anything has connected: the text session is still live and nothing
 *     has changed. The voice session is opened while the text one is still
 *     up, and the text one is ended only once voice has connected — so a
 *     refusal (denied, no microphone, busy) leaves the visitor exactly where
 *     they were, typing, with one status line and no toast.
 *   - Typing during voice works: `sendUserMessage` is on BaseConversation.
 *     The platform echoes a typed message as a `user_transcript` too; a
 *     transcript that matches nothing typed is the visitor's SPOKEN turn and
 *     goes on screen from onMessage (typedPendingRef).
 *   - `onModeChange` delivers `speaking` on each accepted audio chunk and
 *     `listening` when playback finishes (the output worklet's `process
 *     finished`) or on an interruption; `onInterruption` delivers the
 *     event id being discarded. Both are SET as state here and mapped onto
 *     the same four core states DWEB-147 built for text. No loop: the SDK's
 *     dist schedules no requestAnimationFrame and no interval, and neither
 *     does this file — `getOutputVolume()` / `getInputByteFrequencyData()`
 *     exist on VoiceConversation and are deliberately never called (an
 *     audio-level visualiser on rAF is the DWEB-83 defect).
 *   - Mute is `setMicMuted(true)` on the live voice session (the input
 *     worklet drops the chunks); End voice is end-and-restart again, back
 *     to a text session, the transcript kept.
 *   - The widget passes `connectionType: 'websocket'`, so voice travels
 *     the same host as text (DYNAMITE_RUNTIME_HOSTS) — no LiveKit.
 *
 * THE CAPABILITY IS READ AT SESSION START, from the public widget config
 * (DYNAMITE_CAPABILITY_URL): the mic control renders only when the agent
 * says `text_only: false`. That is the deploy-order safety — the site ships
 * before the owner flips the agent, and nothing appears until he does.
 * Nothing about voice is stored: the "explained once" flag is per
 * conversation, in memory (paragraph 46 of the policy enumerates every key
 * the site stores; this is not one).
 */

/** The DOM event the site's calls to action dispatch. */
export const OPEN_DYNAMITE_EVENT = 'openChat';

/**
 * The mounted surface registers its opener here, so `openDynaMite()` is an
 * ordinary state change rather than the v6.4.8 reach into a shadow root.
 * Returns true if a surface was mounted to open. `null` when none is — a
 * call before mount, or after unmount, opens nothing and says so.
 */
let opener = null;

/** Open the chat panel. Returns true if a mounted surface opened. */
export function openDynaMite() {
  if (!opener) return false;
  opener();
  return true;
}

/**
 * What the panel says before the first message. The substance is
 * DYNAMITE_DESCRIPTION's last clause — processed on the hosted platform, not
 * in the browser — which the privacy policy's assistant section spells out.
 * The embed's default terms text spoke of "recording" on a text-only agent;
 * this replaces it with the sentence the policy actually makes.
 */
const NOTICE =
  'DynaMite answers from the published offers and prices. What you type is sent to and ' +
  'processed on the hosted platform, not in your browser, and is kept there for a limited time. ' +
  'Please do not enter sensitive information.';

/**
 * One register with every other deterministic line on the site (DWEB-115):
 * the contact form reaches "the person who would do the work".
 */
const FAILURE =
  "DynaMite couldn't connect. The contact form reaches the person who would do the work.";

const PLACEHOLDER = 'Ask about the offers, prices or the work…';

/**
 * DWEB-142 (v6.5.2): the one line beside the launcher that says the head is
 * a chat worth opening. In the site's register — it says what the chat
 * knows, not "Hi there! 👋" — and the chat's own greeting says who it is.
 */
const BUBBLE_COPY = 'Ask about the offers and what they cost.';

/**
 * Whether this visitor dismissed the bubble. localStorage, like the analytics
 * choice, because "stays dismissed for that visitor" has to survive a reload
 * and a return visit. Functional state, not tracking — nothing reads it but
 * this component. (The AVATAR selection, by contrast, is deliberately NOT
 * persisted — see DynamiteAvatar.js; the two are different requirements.)
 */
const BUBBLE_DISMISSED_KEY = 'dynamiteBubbleDismissed';

function readBubbleDismissed() {
  try {
    return localStorage.getItem(BUBBLE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeBubbleDismissed() {
  try {
    localStorage.setItem(BUBBLE_DISMISSED_KEY, '1');
  } catch {
    // Storage unavailable — the dismissal holds for this page view anyway.
  }
}

/* ------------------------------------------------------------------ */
/* Session                                                             */
/* ------------------------------------------------------------------ */

/**
 * Start a text-only session against the web agent. The SDK is imported here,
 * not at the top of the module, so its chunk is fetched (from this origin)
 * only when a visitor starts a conversation.
 */
async function startSession(callbacks, { textOnly = true } = {}) {
  const { Conversation } = await import('@elevenlabs/client');
  return Conversation.startSession({
    agentId: DYNAMITE_AGENT_ID,
    textOnly,
    connectionType: 'websocket',
    // The SDK's default delays Android by 3 s so the audio manager can switch
    // modes. A text session has no audio; a voice session keeps the default.
    ...(textOnly ? { connectionDelay: { default: 0, android: 0, ios: 0 } } : {}),
    ...callbacks,
  });
}

/* ------------------------------------------------------------------ */
/* Voice (DWEB-130, v6.6.3)                                            */
/* ------------------------------------------------------------------ */

/**
 * What the panel says BEFORE the browser's microphone prompt — the owner's
 * copy, verbatim, with the provider and the retention period taken from
 * config/dynamite.js so it cannot drift from the privacy policy (the policy
 * spec holds both to the same constants). Shown once per conversation.
 */
export const MIC_EXPLANATION =
  `DynaMite can listen. Tap the mic and your browser will ask permission. What you say is sent to ${DYNAMITE_PROVIDER} ` +
  `to be transcribed and answered; the audio and the transcript are kept for ${DYNAMITE_RETENTION_DAYS} days, then deleted. ` +
  'You can type instead at any time.';

/** The status line when voice does not start. Each returns the visitor to text; none is a toast. */
const VOICE_NOTES = {
  denied: "The microphone wasn't allowed. You can keep typing.",
  noMic: 'No microphone was found. You can keep typing.',
  busy: 'The microphone is in use by another app. You can keep typing.',
  failed: "Voice couldn't start. You can keep typing.",
};

/** Which note a getUserMedia / session failure earns, by the DOMException's name. */
function voiceNoteFor(err) {
  const name = (err && err.name) || '';
  if (/NotAllowedError|PermissionDeniedError|SecurityError/.test(name)) return 'denied';
  if (/NotFoundError|DevicesNotFoundError|OverconstrainedError/.test(name)) return 'noMic';
  if (/NotReadableError|AbortError/.test(name)) return 'busy';
  return 'failed';
}

/**
 * Whether THIS page can open a microphone at all: a secure context with
 * getUserMedia and AudioWorklet. Read LIVE at the decision point (the
 * DWEB-89 lesson), never from state.
 */
function canUseMicrophone() {
  try {
    return !!(
      window.isSecureContext &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof window.AudioWorkletNode === 'function'
    );
  } catch {
    return false;
  }
}

/**
 * The agent's capability, read at session start (see DYNAMITE_CAPABILITY_URL
 * in config/dynamite.js). Anything but an explicit `text_only: false` — a
 * refusal, a timeout, a missing field, an insecure page — reads as
 * text-only, and the mic is not offered. Nothing is cached.
 */
async function readVoiceCapability() {
  if (!canUseMicrophone()) return false;
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 6000);
  try {
    const res = await fetch(DYNAMITE_CAPABILITY_URL, { signal: ctl.signal, credentials: 'omit', cache: 'no-store' });
    if (!res.ok) return false;
    const body = await res.json();
    return !!(body && body.widget_config && body.widget_config.text_only === false);
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/** Whitespace- and case-insensitive equality, for matching the platform's echo of a typed message. */
const sameText = (a, b) => String(a || '').replace(/\s+/g, ' ').trim().toLowerCase() === String(b || '').replace(/\s+/g, ' ').trim().toLowerCase();

/* ------------------------------------------------------------------ */
/* Styles — no keyframes, no transforms (see DWEB-83 and the hover trap) */
/* ------------------------------------------------------------------ */

/*
 * The bottom-right corner is a crowded z-index neighbourhood: Footer 1000,
 * the analytics banner 1001 — a full-width bar that keeps its text and
 * buttons clear of this corner with its own right padding, on the premise
 * that the launcher is ABOVE it. It has to be: at 1000 the banner intercepts
 * every click on the launcher, so a first-time visitor (who by definition
 * sees both) cannot open the chat — dynamite.spec.js found exactly that, and
 * it opens the chat with the banner up on purpose. So the whole surface sits
 * at 1002, above the banner and below the 1003 the old model-consent overlay
 * used, which is the window ConsentBanner.js documents.
 *
 * MAXIMIZED (DWEB-141) the whole surface rises to 10001 — one above the site
 * header (Header.js: 10000). Measured on the first run of
 * dynamite-panel.spec.js: at 1002 the maximized panel's own header sat under
 * the site header, and its Restore and Close buttons could not be clicked —
 * Playwright reported the nav's "Contact" link intercepting pointer events.
 * A maximized chat is the visitor's whole attention; it covers the header
 * until Restore or Escape, and the surface drops back to 1002.
 */
/** The reveal's open radius; see the Panel comment (DWEB-146). */
const REVEAL_RADIUS = '170%';
/** Root's column gap, between the panel and the launcher; the reveal anchors on it. */
const LAUNCHER_GAP = 12;

const Root = styled.div`
  position: fixed;
  right: 20px;
  /* DWEB-183: + the keyboard's height while it is open, 0px otherwise. */
  bottom: calc(20px + var(--dynamite-kb-inset, 0px));
  z-index: ${(p) => (p.$maximized ? 10001 : 1002)};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${LAUNCHER_GAP}px;
  pointer-events: none;

  @media (max-width: 768px) {
    right: 12px;
    bottom: calc(12px + var(--dynamite-kb-inset, 0px));
  }
`;

/**
 * The Suspense fallback for the avatar launcher: the v6.5.0 image button at
 * the canvas's size (120px, 80px on a phone), so the corner does not jump
 * when the chunk lands. It carries the same test id, label and expanded
 * state, so a click before the chunk arrives still opens the panel.
 */
const LauncherFallback = styled.button`
  pointer-events: auto;
  width: 120px;
  height: 120px;
  padding: 0;
  border-radius: 50%;
  border: 2px solid rgba(245, 158, 11, 0.7);
  background: rgba(15, 16, 34, 0.92);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45), 0 0 18px rgba(245, 158, 11, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover,
  &:focus-visible {
    border-color: rgba(245, 158, 11, 1);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5), 0 0 26px rgba(245, 158, 11, 0.45);
    outline: none;
  }

  img {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    img {
      width: 48px;
      height: 48px;
    }
  }
`;

/*
 * DWEB-142 (v6.5.2): the launcher's row. The bubble sits to the LEFT of the
 * avatar, in a column of its own so a second control — the call bubble
 * DWEB-140 will add once the number is published — has its place above or
 * below without touching this layout. The avatar is not moved by it: the row
 * is right-aligned in Root, the launcher keeps its size, and
 * dynamite-bubble.spec.js measures the launcher's box with and without the
 * bubble.
 */
const LauncherRow = styled.div`
  display: flex;
  /* DWEB-140: the launcher sits on the row's BOTTOM edge, so its position
     does not depend on the bubble's height. With the call route the bubble
     is two lines, and on a phone (200px wide, 12px type) it is taller than
     the 80px launcher: centring both in the row moved the launcher 3.6px
     when the bubble left — dynamite-bubble.spec.js's "the launcher is where
     it is without the bubble" caught it. The bubble centres itself (below). */
  align-items: flex-end;
  justify-content: flex-end;
  gap: 12px;
  pointer-events: none;
`;

const Callouts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  align-self: center;
  gap: 8px;
  pointer-events: none;
`;

/*
 * STATIC, ON PURPOSE. No keyframes, no transition on transform, no
 * will-change, no backdrop-filter. DWEB-83's census counts rAF callers and
 * cannot see a CSS animation (the compositor runs it); the cost of an
 * infinite pulse lands on DWEB-86 instead — "41 permanent will-change
 * layers", and this element sits above everything else on the page. And a
 * throbbing chat bubble is the growth-hack register this practice left with
 * the embed. A solid background instead of glass for the same reason:
 * DWEB-86 counts backdrop-filters too.
 */
const Callout = styled.div`
  pointer-events: auto;
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 280px;
  padding: 8px 8px 8px 12px;
  border-radius: 14px;
  border: 1px solid rgba(245, 158, 11, 0.55);
  background: rgba(15, 16, 34, 0.96);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  color: #e5e7eb;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  line-height: 1.4;

  /* The tail, pointing at the avatar. A border triangle: static, no layer. */
  &::after {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    margin-top: -6px;
    border-width: 6px 0 6px 8px;
    border-style: solid;
    border-color: transparent transparent transparent rgba(245, 158, 11, 0.55);
  }

  @media (max-width: 768px) {
    max-width: 200px;
    font-size: 12px;
  }
`;

const CalloutLine = styled.button`
  border: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: #fbbf24;
    outline: none;
  }
`;

/*
 * DWEB-140 (v6.6.4): the bubble's two lines stack in one column — the chat
 * line (a button) and the call route (a tel: anchor). ONE bubble, ONE
 * dismissal, ONE storage key: a second bubble with its own dismissed state
 * would be a sixth key and a false privacy policy (DWEB-143). Same static
 * rule as the bubble itself — a colour on hover and nothing on transform.
 */
const CalloutLines = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CalloutCall = styled.span`
  color: rgba(229, 231, 235, 0.85);
  font-size: 12px;

  a {
    color: #fbbf24;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
  }

  a:hover,
  a:focus-visible {
    color: #fde68a;
    text-decoration: underline;
    outline: none;
  }
`;

const CalloutDismiss = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: #e5e7eb;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.18);
    outline: none;
  }
`;

/*
 * Maximized (DWEB-141): the panel leaves the column and takes the viewport
 * less a margin, over the launcher (the launcher keeps its canvas and its
 * synced avatar; the panel's z-index paints above it inside Root's stacking
 * context). The embed's resize button did the equivalent. Restore returns
 * the panel to the column at its normal size.
 */
const maximizedPanel = css`
  position: fixed;
  top: 20px;
  right: 20px;
  /* DWEB-183: maximized is the worse case — the composer is flush with the
     bottom edge, so the keyboard covers it completely. */
  bottom: calc(20px + var(--dynamite-kb-inset, 0px));
  left: 20px;
  width: auto;
  height: auto;
  max-width: none;
  max-height: none;

  @media (max-width: 768px) {
    top: 0;
    right: 0;
    bottom: var(--dynamite-kb-inset, 0px);
    left: 0;
    /* Repeated here on purpose: the panel's own phone media block (width,
       height, max-height) is emitted AFTER the main block, so the values
       above would lose to it at this width — measured: 351px wide, i.e.
       calc(100vw - 24px), on the first run. */
    width: auto;
    height: auto;
    max-width: none;
    max-height: none;
    border-radius: 0;
  }
`;

/**
 * DWEB-150: the panel's tint FLOOR — the minimum dark alpha under the glass.
 * Below it the text over the page's light section falls under 4.5:1
 * (measured: at 0.10 the status line reads ~1.9:1 over white); above ~0.5
 * the glass stops being glass. dynamite-glass.spec.js asserts the computed
 * alpha sits between the floor and 0.5, and measures the contrast that
 * results.
 */
export const PANEL_TINT_ALPHA = 0.45;
/** The dark the tint and the text surfaces are made of — the site's midnight, not black. */
const INK = '10, 12, 28';

const Panel = styled.section`
  pointer-events: auto;
  position: relative;
  z-index: 1002;
  width: 380px;
  max-width: calc(100vw - 24px);
  height: 540px;
  /* the launcher below it is 120px + the 12px gap + 20px top and bottom */
  /* DWEB-183: the dvh term is untouched; the keyboard is subtracted from it. */
  max-height: calc(100dvh - 172px - var(--dynamite-kb-inset, 0px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 20px;
  /* DWEB-150: the site's glass. The tint at its floor, the depth ramp over
     it (light top-left, faintly dark bottom-right — the orb's shell), the
     top-lit border, and the lit-shell backdrop: blur, saturation, a touch
     of brightness so dark does not blur into dark. */
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-top-color: rgba(255, 255, 255, 0.7);
  border-bottom-color: rgba(255, 255, 255, 0.18);
  background-color: rgba(${INK}, ${PANEL_TINT_ALPHA});
  background-image: linear-gradient(165deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.02) 35%, rgba(0, 0, 0, 0.1) 100%);
  -webkit-backdrop-filter: blur(24px) saturate(220%) brightness(1.15);
  backdrop-filter: blur(24px) saturate(220%) brightness(1.15);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.55), inset 0 0 30px rgba(255, 255, 255, 0.06);
  color: #e5e7eb;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  /*
   * DWEB-146: the reveal. The circle is anchored on the launcher's center —
   * the panel's right edge is the launcher's right edge (Root aligns the
   * column to the end), and the launcher sits one gap below the panel — and
   * it grows from 0 to a radius that covers the far corner of the largest
   * panel (maximized on a desktop: the far corner is ~1450px from the
   * anchor; 170% of circle()'s reference length for that box is ~1750px).
   * The transition is on clip-path ONLY; the box never changes. Under
   * prefers-reduced-motion index.css collapses the duration; the widget
   * also skips straight to the end state (see close / openPanel).
   */
  --launcher: ${ORB_SIZE}px;
  clip-path: circle(${(p) => (p.$revealed ? REVEAL_RADIUS : '0px')} at calc(100% - var(--launcher) / 2) calc(100% + ${LAUNCHER_GAP}px + var(--launcher) / 2));
  transition: clip-path 0.52s cubic-bezier(0.3, 0.7, 0.2, 1);

  @media (max-width: 768px) {
    --launcher: ${ORB_SIZE_PHONE}px;
    width: calc(100vw - 24px);
    height: 70dvh;
    max-height: calc(100dvh - 116px - var(--dynamite-kb-inset, 0px));
    border-radius: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(p) => (p.$maximized ? maximizedPanel : '')}

  /* DWEB-173: rgba(10, 12, 28, 0.45) over the black ground
     (0.45 x (10, 12, 28) = (4.5, 5.4, 12.6)). The depth ramp
     (background-image) is kept — a painted gradient, not translucency — and
     the contrast this yields is MEASURED in dynamite-glass.spec.js as its own
     reading rather than inherited from the glass one. */
  ${solidWhenReducedTransparency('rgb(5, 5, 13)')}
`;


/*
 * DWEB-146: the travelling core — the orb's core on its way to the header,
 * where DynamiteCore takes over. A fixed 18px dot; `left`/`top` transition
 * from the orb's core to the header core's center, .5s, once per open from
 * the orb face; unmounted when it arrives (transitionend, with a timer as
 * the fallback). No backdrop, no will-change, no keyframe.
 */
const Fly = styled.div`
  position: fixed;
  z-index: 1003;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  pointer-events: none;
  left: var(--x, 0px);
  top: var(--y, 0px);
  transform: translate(-50%, -50%);
  background: radial-gradient(circle at 50% 50%, #fffdf6 0%, #fff8dc 26%, rgba(251, 191, 36, 0.95) 44%, rgba(245, 158, 11, 0) 100%);
  box-shadow: 0 0 18px 6px rgba(245, 158, 11, 0.35);
  transition: left 0.5s cubic-bezier(0.3, 0.7, 0.2, 1), top 0.5s cubic-bezier(0.3, 0.7, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* DWEB-150: the header is a text surface — its own dark band over the glass, so the who line holds 4.5:1 over the light section. */
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(15, 20, 50, 0.7);
  border-bottom: 1px solid var(--glass-border);

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    text-shadow: 0 0 15px rgba(30, 64, 175, 0.8);
  }

  small {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: rgba(229, 231, 235, 0.8);
  }
`;

/** The title beside the header core (DWEB-146); it takes the room the actions leave. */
const HeaderWho = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const QuietButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: #e5e7eb;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.18);
    outline: none;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  /*
    DWEB-183 — the chat "is not scrollable" because the page scrolls instead.

    MEASURED at 375x812 with the greeting on screen: overscroll-behavior
    computed to auto on this list, on the panel, on body AND on html, so
    nothing bounded the chain anywhere. A short conversation has NOTHING to
    scroll -- scrollHeight === clientHeight === 411 -- so a drag inside the
    panel went straight through to the document, which is 23,518px tall: a
    400px wheel over the list moved window.scrollY from 0 to 400. The chat
    looks frozen while the site slides behind it.

    contain, not none: none also suppresses the rubber-band, and that
    bounce is how a visitor tells a list that has ENDED from one that has
    DIED -- which is the very confusion this ticket is about.

    NOT min-height: 0. The ticket originally prescribed it and it is a
    no-op here: the content-based automatic minimum size applies only when
    computed overflow is visible, and for a scroll container it is zero.
    This box declares overflow-y: auto, so its min-height: auto already
    resolves to 0 -- measured, 429 tall against 1,772 of content.
  */
  overscroll-behavior: contain;
  padding: 14px 14px 6px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;
  line-height: 1.55;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
  }
`;

const Bubble = styled.div`
  align-self: ${(p) => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
  /* DWEB-150: bubbles keep their own backgrounds so the text has a surface
     over the glass; the agent's is the ink at 72%, which holds 4.5:1 over
     the light section (measured in dynamite-glass.spec.js). */
  background: ${(p) => (p.$role === 'user' ? 'rgba(59, 130, 246, 0.9)' : `rgba(${INK}, 0.72)`)};
  border: 1px solid ${(p) => (p.$role === 'user' ? 'rgba(59, 130, 246, 0.6)' : 'rgba(255, 255, 255, 0.18)')};
  color: #ffffff;

  p {
    margin: 0 0 0.5em;
  }
  p:last-child {
    margin-bottom: 0;
  }
  a {
    color: #fbbf24;
  }
  ul,
  ol {
    margin: 0.25em 0 0.5em;
    padding-left: 1.25em;
  }
`;

const Notice = styled.div`
  margin: auto 0 0;
  padding: 12px 14px;
  border-radius: 12px;
  /* DWEB-150: a text surface — the ink under the amber, not the amber alone. */
  background: linear-gradient(rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.1)), rgba(${INK}, 0.72);
  border: 1px solid rgba(245, 158, 11, 0.35);
  font-size: 13px;
  line-height: 1.5;

  p {
    margin: 0 0 10px;
  }
  a {
    color: #fbbf24;
  }
`;

const Primary = styled.button`
  border: 1px solid rgba(245, 158, 11, 0.8);
  background: rgba(245, 158, 11, 0.9);
  color: #111827;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(251, 191, 36, 1);
    outline: none;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/* DWEB-150: the status line and the composer share one dark band at the
   bottom, the header's counterpart; the status text is a little more
   opaque than before so it holds 4.5:1 over the light section. */
const Status = styled.div`
  padding: 6px 16px 4px;
  font-size: 12px;
  color: rgba(229, 231, 235, 0.92);
  min-height: 18px;
  background: rgba(${INK}, 0.6);
  border-top: 1px solid rgba(255, 255, 255, 0.16);

  small {
    display: block;
    color: rgba(229, 231, 235, 0.8);
  }
`;

const Composer = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 12px;
  background: rgba(${INK}, 0.6);

  input {
    flex: 1;
    min-width: 0;
    height: 42px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.92);
    color: #111827;
    font-size: 14px;
  }
  input:disabled {
    opacity: 0.6;
  }
`;

const Send = styled.button`
  height: 42px;
  min-width: 42px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.92);
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/*
 * DWEB-130: the mic control, in the slot the composer has kept since v6.5.0.
 * The Send button's size and surface; amber while voice is on and the mic
 * is open. No keyframe, no transform, no level meter — speaking is a state
 * on the header core, not a bar on this button.
 */
const MicButton = styled.button`
  height: 42px;
  min-width: 42px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$live ? 'rgba(245, 158, 11, 0.9)' : 'rgba(255, 255, 255, 0.3)')};
  background: ${(p) => (p.$live ? 'rgba(245, 158, 11, 0.92)' : 'rgba(255, 255, 255, 0.92)')};
  color: ${(p) => (p.$live ? '#111827' : '#1e40af')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/** The explanation card's two answers, side by side. */
const ExplainerActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

/** A microphone, drawn inline; a slash across it when muted. */
function MicIcon({ muted = false }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
      {muted && <path d="M4 4l16 16" />}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

let nextId = 0;
/** `live` marks the agent bubble a reply is still streaming into (DWEB-147). */
const msg = (role, text, live = false) => ({ id: ++nextId, role, text, live });

/**
 * DWEB-193: if the markdown chunk cannot load (a dropped connection, a stale
 * deploy), the reply is still shown — as plain text — rather than the error
 * reaching the root, which would take the whole page down mid-conversation.
 */
class PlainTextOnFailure extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.text : this.props.children;
  }
}

/** An agent reply: markdown from the lazy chunk, plain text until it lands. */
function ReplyText({ text }) {
  return (
    <PlainTextOnFailure text={text}>
      <Suspense fallback={text}>
        <AgentMarkdown>{text}</AgentMarkdown>
      </Suspense>
    </PlainTextOnFailure>
  );
}

/**
 * Whether the visitor has asked for reduced motion — read LIVE at the
 * decision point, never from state: a flag set by a mount effect is one
 * render stale where it is read (the DWEB-89 lesson). Under `reduce` the
 * orb's core does not travel and the panel opens and closes at its end
 * state, so no transitionend is waited on.
 */
function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** How long the widget waits for a transitionend before finishing the step itself (the transitions are .52s and .5s). */
const TRANSITION_FALLBACK_MS = 800;

export default function DynaMiteWidget() {
  /**
   * The panel's life (DWEB-146): `open` is whether it is MOUNTED; `revealed`
   * is whether its clip-path is at the open radius; `closing` is the reverse
   * transition running before the unmount. An open therefore goes
   * open=true (mounted, clip 0) -> revealed=true (clip grows); a close goes
   * closing=true + revealed=false (clip shrinks) -> transitionend ->
   * open=false. Every spec that asserts the panel "has count 0" after
   * Escape still holds: the unmount is .52s behind the key.
   */
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [closing, setClosing] = useState(false);
  /** The orb's core is travelling to the header (orb face only). */
  const [flying, setFlying] = useState(false);
  /**
   * DWEB-144: the DEFAULT face this page load renders — the constant, or the
   * suite's / a checker's runtime override. Read once; it does not change
   * under a mounted surface.
   */
  const [launcherKind] = useState(() => resolveDynamiteLauncher());
  /**
   * DWEB-146: what the demo's sync toggle has promoted into the launcher —
   * `{ avatar, url }` while sync is on, null otherwise. Fed by the
   * AvatarSyncManager subscription below; in memory, per page load.
   */
  const [synced, setSynced] = useState(null);
  const face = synced ? 'avatar' : launcherKind;
  /**
   * DWEB-141: maximized takes the viewport; closing the panel restores the
   * size, so a fresh open is always the small panel.
   */
  const [maximized, setMaximized] = useState(false);
  /**
   * The session, from the visitor's side:
   *   idle        panel open, nothing started, notice shown
   *   connecting  Start pressed; the SDK chunk and the socket are on their way
   *   live        connected; the agent's greeting arrives on its own
   *   ended       the visitor or the agent ended it; Start begins a new one
   *   failed      the connection was refused or dropped; FAILURE is shown
   */
  const [phase, setPhase] = useState('idle');
  /**
   * Whose turn it is, while live (DWEB-147, v6.6.2): 'ready', 'thinking' (a
   * message is out and no `start` has arrived) or 'speaking' (a reply is
   * streaming in, between `start` and `stop`). It is mapped onto the header
   * core's `state` below — `coreState` — with 'listening' derived from the
   * composer having focus. DWEB-130 (voice, v6.6.3) drives the same four
   * states from the SDK's `onModeChange`; the composer row leaves the slot
   * for its control. The input is never disabled on 'thinking': a disabled
   * input is not a safe failure mode (DWEB-89).
   */
  const [turn, setTurn] = useState('ready');
  /** Whether the composer has focus — 'listening' is derived from it. */
  const [inputFocused, setInputFocused] = useState(false);
  /**
   * DWEB-130 — voice, opt-in per conversation:
   *   voiceCapable  the agent said `text_only: false` at this session's start
   *                 AND this page can open a microphone; the mic control's gate
   *   voice         'off' | 'explaining' (the card is up) | 'starting'
   *                 (a voice session is connecting; the browser may be asking)
   *                 | 'on' | 'ending' (a text session is connecting to take over)
   *   voiceMode     the SDK's onModeChange, 'listening' | 'speaking'
   *   micMuted      setMicMuted on the live voice session
   *   voiceNote     the status line's one sentence when voice did not start
   */
  const [voiceCapable, setVoiceCapable] = useState(false);
  const [voice, setVoice] = useState('off');
  const [voiceMode, setVoiceMode] = useState('listening');
  const [micMuted, setMicMuted] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);
  const [voiceDetail, setVoiceDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detail, setDetail] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [draft, setDraft] = useState('');

  /**
   * DWEB-142: the bubble shows only once the analytics consent choice is
   * made (ConsentBanner.js at 1001 and this surface at 1002 are already two
   * things competing for a first-time visitor), only until it is dismissed
   * (persisted), and only until the chat has been opened once this page
   * load — after that it has done its job. Read live from storage at mount;
   * the banner's decision arrives as CONSENT_CHANGED_EVENT.
   */
  const [consentDecided, setConsentDecided] = useState(() => getStoredConsent() !== null);
  const [bubbleDismissed, setBubbleDismissed] = useState(() => readBubbleDismissed());
  const [openedOnce, setOpenedOnce] = useState(false);

  const conversationRef = useRef(null);
  /**
   * DWEB-130: which session's callbacks count. Every session opened gets a
   * fresh token from `sessionSeqRef`; `activeTokenRef` names the one this
   * component listens to. A voice session replaces the text one only once
   * it has connected, and the text session's own onDisconnect — fired when
   * it is then ended — is ignored because its token is no longer active.
   */
  const sessionSeqRef = useRef(0);
  const activeTokenRef = useRef(0);
  /** DWEB-130: the explanation card has been shown this conversation (memory only — not a storage key). */
  const explainedRef = useRef(false);
  /** DWEB-130: typed messages whose `user_transcript` echo has not arrived yet, in order (voice sessions). */
  const typedPendingRef = useRef([]);
  /** The id of the agent bubble a reply is streaming into; null between turns (DWEB-147). */
  const liveIdRef = useRef(null);
  /**
   * DWEB-147: whether the transcript should follow new text. True while the
   * visitor is at (or within a few pixels of) the bottom; false once they
   * scroll up to re-read, so a streaming reply does not fight them. Reset to
   * true by their own send, which always brings the bottom into view.
   */
  const stickToBottomRef = useRef(true);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const startRef = useRef(null);
  const panelRef = useRef(null);
  const flyRef = useRef(null);
  const orbRef = useRef(null);
  /** Where the travelling core starts: the orb's core, measured at the click — or, on the return flight (DWEB-150), the header core. */
  const flyStartRef = useRef(null);
  /** DWEB-150: the flight in progress is the RETURN one (header -> orb); the layout effect below picks its destination from this. */
  const flyBackRef = useRef(false);
  /** The current opener, for the mount-time listeners below. */
  const openPanelRef = useRef(null);
  /**
   * DWEB-142: opening from the bubble unmounts the button the visitor just
   * pressed, so focus would fall to <body> — outside Root, where Escape is
   * handled — and a keyboard visitor could not close what they had opened.
   * The effect below moves focus onto the panel's first action instead.
   * Measured, not assumed: the first run of dynamite-bubble.spec.js pressed
   * Escape after opening from the bubble and the panel stayed.
   */
  const focusPanelOnOpenRef = useRef(false);

  const append = useCallback((role, text) => {
    setMessages((prev) => [...prev, msg(role, text)]);
  }, []);

  /**
   * DWEB-146: the face follows the demo's sync toggle through the manager.
   * `avatar_changed` with sync on promotes the head into the launcher;
   * `avatar_reset` (or any event that says sync is off) returns the default
   * face. The manager replays its state to a new subscriber after 50 ms; the
   * synchronous read after subscribing covers the demo having published
   * before this surface mounted. The head itself (DynamiteAvatar.js)
   * subscribes under its own id and loads the GLB; this subscription only
   * decides WHICH face is mounted.
   *
   * THE RACE (avatar.spec.js at 83507c0:305, fixed in v6.5.1): sync on then
   * off before the head has finished loading its GLB. With the orb as the
   * default, "off" UNMOUNTS the head synchronously — the load that was in
   * flight completes into a component that is no longer rendered, and
   * nothing lands on screen. With the avatar as the default, the head stays
   * mounted and v6.5.1's one-pending-event fix inside it stands. Both are
   * asserted: the orb's in dynamite-orb.spec.js, the avatar's in avatar.spec.js.
   */
  useEffect(() => {
    const onSync = (event) => {
      if (event.type === 'avatar_changed' && event.syncEnabled !== false && event.avatar && event.url) {
        setSynced({ avatar: event.avatar, url: event.url });
      } else if (event.type === 'avatar_reset' || event.syncEnabled === false) {
        setSynced(null);
      }
    };
    avatarSyncManager.subscribe('dynamiteWidget', onSync);
    const now = avatarSyncManager.getCurrentState();
    if (now.syncEnabled && now.selectedAvatar && now.selectedUrl) {
      setSynced({ avatar: now.selectedAvatar, url: now.selectedUrl });
    }
    return () => avatarSyncManager.unsubscribe('dynamiteWidget');
  }, []);

  /**
   * Open the panel: mount it pre-sized with its clip at zero, and — from the
   * orb face, with motion allowed — start the core's journey from where the
   * orb's core is now. The reveal itself starts in the layout effect below,
   * once the start state has been committed.
   */
  const openPanel = useCallback(() => {
    if (open && !closing) return;
    // DWEB-193: start fetching the markdown chunk now, so it is in before the
    // first reply. A failure here is left to ReplyText's plain-text fallback.
    loadAgentMarkdown().catch(() => {});
    if (closing) {
      // Reopened mid-close: reverse the reverse. A return flight in
      // progress (DWEB-150) is cut; the header core simply shows again.
      setClosing(false);
      setFlying(false);
      setRevealed(true);
      return;
    }
    const orbEl = orbRef.current;
    if (face === 'orb' && orbEl && !prefersReducedMotion()) {
      flyStartRef.current = orbCoreCenter(orbEl);
      flyBackRef.current = false;
      setFlying(true);
    } else {
      flyStartRef.current = null;
    }
    setRevealed(false);
    setOpen(true);
  }, [open, closing, face]);
  openPanelRef.current = openPanel;

  /**
   * The reveal. Runs after the panel is in the DOM with its clip at zero and
   * the travelling core at its start. A forced layout read commits that
   * state (the prototype's `getBoundingClientRect()` trick), so the changes
   * that follow — the clip to its open radius, the core to the header — are
   * TRANSITIONS from a committed start, not a jump. No frame is scheduled.
   */
  useLayoutEffect(() => {
    if (!open || revealed || closing) return;
    const panel = panelRef.current;
    if (!panel) return;
    panel.getBoundingClientRect();
    const fly = flyRef.current;
    if (fly && !flyBackRef.current) {
      fly.getBoundingClientRect();
      const core = panel.querySelector('[data-testid="dynamite-core"]');
      if (core) {
        const c = core.getBoundingClientRect();
        fly.style.setProperty('--x', `${c.left + c.width / 2}px`);
        fly.style.setProperty('--y', `${c.top + c.height / 2}px`);
      }
    }
    setRevealed(true);
  }, [open, revealed, closing]);

  /**
   * DWEB-150: THE RETURN FLIGHT. Mounted at the header core's center by
   * `close`, the travelling dot is sent to the orb's core here — the same
   * forced-layout-then-set as the outbound flight, so the change is a
   * transition from a committed start. The orb's core layer stays hidden
   * until the dot lands (coreHidden below).
   */
  useLayoutEffect(() => {
    if (!flying || !flyBackRef.current) return;
    const fly = flyRef.current;
    const orbEl = orbRef.current;
    if (!fly || !orbEl) return;
    fly.getBoundingClientRect();
    const c = orbCoreCenter(orbEl);
    fly.style.setProperty('--x', `${c.x}px`);
    fly.style.setProperty('--y', `${c.y}px`);
  }, [flying]);

  // The travelling core is unmounted on arrival; a timer covers a transitionend that never comes.
  useEffect(() => {
    if (!flying) return undefined;
    const t = setTimeout(() => setFlying(false), TRANSITION_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [flying]);

  const close = useCallback(() => {
    if (!open || closing) return;
    setMaximized(false);
    if (!revealed || prefersReducedMotion()) {
      setFlying(false);
      setRevealed(false);
      setOpen(false);
      return;
    }
    // DWEB-150: from the orb face, the core flies back to the sphere while
    // the clip closes; the header core hides for the flight (hidden={flying}).
    const orbEl = orbRef.current;
    const panel = panelRef.current;
    const core = panel && panel.querySelector('[data-testid="dynamite-core"]');
    if (face === 'orb' && orbEl && core) {
      const c = core.getBoundingClientRect();
      flyStartRef.current = { x: c.left + c.width / 2, y: c.top + c.height / 2 };
      flyBackRef.current = true;
      setFlying(true);
    } else {
      setFlying(false);
    }
    setClosing(true);
    setRevealed(false);
  }, [open, closing, revealed, face]);

  // The panel is unmounted when its clip has closed; a timer covers a transitionend that never comes.
  useEffect(() => {
    if (!closing) return undefined;
    const t = setTimeout(() => {
      setClosing(false);
      setOpen(false);
    }, TRANSITION_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [closing]);

  const onPanelTransitionEnd = useCallback((e) => {
    if (!closing || e.target !== panelRef.current || e.propertyName !== 'clip-path') return;
    setClosing(false);
    setOpen(false);
  }, [closing]);

  const onFlyTransitionEnd = useCallback((e) => {
    if (e.propertyName === 'left') setFlying(false);
  }, []);

  // The site's calls to action open the panel through this.
  useEffect(() => {
    opener = () => openPanelRef.current();
    const onOpen = () => openPanelRef.current();
    window.addEventListener(OPEN_DYNAMITE_EVENT, onOpen);
    return () => {
      window.removeEventListener(OPEN_DYNAMITE_EVENT, onOpen);
      opener = null;
    };
  }, []);

  useEffect(() => {
    const onConsent = () => setConsentDecided(getStoredConsent() !== null);
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onConsent);
  }, []);

  useEffect(() => {
    if (open) setOpenedOnce(true);
  }, [open]);

  const dismissBubble = useCallback(() => {
    writeBubbleDismissed();
    setBubbleDismissed(true);
  }, []);

  const openFromBubble = useCallback(() => {
    focusPanelOnOpenRef.current = true;
    openPanelRef.current();
  }, []);

  useEffect(() => {
    if (!open || !focusPanelOnOpenRef.current) return;
    focusPanelOnOpenRef.current = false;
    const target = startRef.current || inputRef.current;
    if (target) target.focus();
  }, [open]);

  // End the session with the component, never leave a socket behind.
  useEffect(() => () => {
    const c = conversationRef.current;
    conversationRef.current = null;
    if (c) c.endSession().catch(() => {});
  }, []);

  // Keep the newest text in view — unless the visitor has scrolled up (DWEB-147).
  useEffect(() => {
    const el = listRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, phase, turn]);

  /*
   * DWEB-183 — a drag beats the stream, because a gesture says more than a
   * distance does.
   *
   * DWEB-147 released the auto-follow pin once the visitor was more than 48px
   * from the bottom, which is right for a wheel and wrong for a finger: at the
   * bottom of the list every touch drag STARTS inside that band, so its first
   * 48px are fought by the stream. MEASURED mid-stream at 375x812, dragging up
   * 30px: the next delta put scrollTop back to the maximum, and the one after
   * that, and the one after that. Dragging 200px behaved correctly (scrollTop
   * held at 1906 while the maximum grew to 2583), which is what shows the band
   * to be the whole of the defect.
   *
   * So the pin is released on POINTER DOWN -- the visitor has taken hold of the
   * list, whatever the distance -- and the distance rule is re-applied when they
   * let go. A click that never moves therefore re-arms on pointerup and
   * auto-follow continues, which a bare "unpin on pointerdown" would have
   * broken for anyone selecting text in a bubble.
   *
   * While a pointer is down the scroll handler does NOT recompute: inside the
   * band it would re-arm mid-drag and slam the visitor back, which is the
   * defect wearing a different hat.
   */
  const pointerHeldRef = useRef(false);

  const recomputeStick = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }, []);

  const onListScroll = useCallback(() => {
    if (pointerHeldRef.current) return;
    recomputeStick();
  }, [recomputeStick]);

  const onListPointerDown = useCallback(() => {
    pointerHeldRef.current = true;
    stickToBottomRef.current = false;
  }, []);

  const onListPointerUp = useCallback(() => {
    pointerHeldRef.current = false;
    recomputeStick();
  }, [recomputeStick]);

  /*
   * DWEB-183 — the software keyboard, which the layout viewport does not know
   * about.
   *
   * MEASURED at 375x812: the composer's bottom edge sits at 708 normally and
   * at 811 maximized — flush with the viewport. Both are positioned against
   * the LAYOUT viewport, and on both iOS Safari and Chrome Android the
   * keyboard's default behaviour is `resizes-visual`: it shrinks the VISUAL
   * viewport and leaves the layout viewport alone. So dvh, svh, 100% and every
   * fixed box keep their pre-keyboard height and the keyboard is simply drawn
   * over the composer. Maximized is the worse case because it is flush.
   *
   * NOT `interactive-widget=resizes-content`, which the ticket first
   * prescribed: Chrome 108+ and Firefox 132+ only. WebKit has not shipped it,
   * and the report is from an iPhone.
   *
   * So the keyboard's height is measured from window.visualViewport and
   * published as ONE custom property that the Root and the Panel subtract.
   * The property is only ever written while the panel is open, and only when
   * visualViewport exists; every consumer defaults it to 0px, which makes the
   * no-keyboard case byte-identical to the arithmetic that was there before.
   *
   * The `var(..., 0px)` fallbacks here are deliberate and are NOT the second
   * copy that index.css's --glass-bg block warns against: absence is a real
   * state of this property (panel closed, or no visualViewport), and 0px is
   * its specified meaning rather than a guess at a value declared elsewhere.
   *
   * COALESCED, because visualViewport `scroll` fires at frame rate while the
   * keyboard animates: at most one style write per frame, and no React state,
   * so a keyboard animation cannot re-render the transcript. It schedules a
   * frame only in response to an event — the rAF census with the panel open
   * and no viewport events still reads zero callers.
   */
  useEffect(() => {
    if (!open) return undefined;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    // No visualViewport: today's behaviour, untouched. Desktop is unaffected.
    if (!vv) return undefined;

    const root = document.documentElement;
    let frame = 0;
    const write = () => {
      frame = 0;
      // The layout viewport is the thing the keyboard does not move, so the
      // gap below the visual viewport IS the keyboard (plus any accessory bar).
      const inset = Math.max(0, Math.round(root.clientHeight - vv.height - vv.offsetTop));
      root.style.setProperty('--dynamite-kb-inset', `${inset}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(write);
    };

    write();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    return () => {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      if (frame) cancelAnimationFrame(frame);
      root.style.removeProperty('--dynamite-kb-inset');
    };
  }, [open]);

  // Focus follows the phase: the notice's button first, then the composer.
  useEffect(() => {
    if (open && phase === 'live' && inputRef.current) inputRef.current.focus();
  }, [open, phase]);

  /**
   * Open a session — text or voice — with this component's callbacks, and
   * make it the active one. Resolves with the SDK's conversation; rejects as
   * startSession does (for voice: the permission prompt's denial, a missing
   * microphone, then the socket). The caller decides what replaces what.
   */
  const openSession = useCallback(async (textOnly) => {
    const token = ++sessionSeqRef.current;
    activeTokenRef.current = token;
    const mine = () => activeTokenRef.current === token;
    const conversation = await startSession({
      onConnect: ({ conversationId: id }) => { if (mine()) setConversationId(id); },
      // DWEB-147: the reply, as it streams. `start` opens a live bubble
      // (and the turn is 'speaking'), each `delta` appends to it, `stop`
      // ends the turn; onMessage below then reconciles the bubble to the
      // canonical text. A delta with no start (never seen; defensive)
      // opens the bubble itself.
      onAgentChatResponsePart: ({ text, type }) => {
        if (!mine()) return;
        if (type === 'start' || (type === 'delta' && liveIdRef.current === null)) {
          const id = ++nextId;
          liveIdRef.current = id;
          setMessages((prev) => [...prev, { id, role: 'agent', text: type === 'delta' ? text : '', live: true }]);
          setTurn('speaking');
          return;
        }
        const id = liveIdRef.current;
        if (id === null) return;
        if (type === 'delta') {
          setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: m.text + text } : m)));
        } else if (type === 'stop') {
          // The turn ends here; the bubble is no longer streaming. Its id is
          // kept so the canonical text below can still find it.
          setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, live: false } : m)));
          setTurn('ready');
        }
      },
      // The agent says it is composing (never observed from this agent,
      // 2026-09-06; the platform declares it): thinking, until a part lands.
      onAgentTyping: ({ is_typing: typing }) => {
        if (mine() && typing) setTurn((t) => (t === 'ready' ? 'thinking' : t));
      },
      onMessage: ({ role, message }) => {
        if (!mine()) return;
        if (role !== 'agent') {
          // The server echoes the visitor's own message as a user transcript.
          // In a text session that is the only kind, and it is already on
          // screen from send(). In a voice session (DWEB-130) a transcript
          // that matches nothing typed is the visitor's SPOKEN turn: it goes
          // on screen here, and the agent is thinking until its reply.
          if (textOnly) return;
          const pending = typedPendingRef.current;
          const i = pending.findIndex((t) => sameText(t, message));
          if (i >= 0) { pending.splice(i, 1); return; }
          stickToBottomRef.current = true;
          append('user', message);
          setTurn('thinking');
          return;
        }
        setTurn('ready');
        const id = liveIdRef.current;
        liveIdRef.current = null;
        if (id !== null) {
          // RECONCILE: the canonical text wins over whatever the deltas built.
          setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: message, live: false } : m)));
        } else {
          append('agent', message);
        }
      },
      onAgentResponseCorrection: ({ corrected_agent_response: corrected }) => {
        if (!mine() || typeof corrected !== 'string') return;
        setMessages((prev) => {
          let i = prev.length - 1;
          while (i >= 0 && prev[i].role !== 'agent') i -= 1;
          if (i < 0) return prev;
          const next = prev.slice();
          next[i] = { ...next[i], text: corrected, live: false };
          return next;
        });
      },
      // DWEB-130: the SDK's mode — speaking while the agent's audio plays,
      // listening when it finishes — and the platform's barge-in. SET, never
      // polled; the header core keys its motion on the resulting state.
      onModeChange: ({ mode }) => { if (mine()) setVoiceMode(mode === 'speaking' ? 'speaking' : 'listening'); },
      onInterruption: () => {
        if (!mine()) return;
        setVoiceMode('listening');
        setTurn('ready');
      },
      onError: (message, context) => {
        if (mine()) setDetail(typeof message === 'string' ? message : String(context || message));
      },
      onDisconnect: (details) => {
        if (!mine()) return;
        conversationRef.current = null;
        if (details.reason === 'error') {
          setPhase('failed');
          setDetail((prev) => prev || details.message || details.closeReason || null);
        } else {
          setPhase('ended');
        }
        setTurn('ready');
        setVoice('off');
        setMicMuted(false);
        liveIdRef.current = null;
        typedPendingRef.current = [];
      },
    }, { textOnly });
    return { token, conversation };
  }, [append]);

  const start = useCallback(async () => {
    if (conversationRef.current) return;
    setPhase('connecting');
    setDetail(null);
    setMessages([]);
    setTurn('ready');
    liveIdRef.current = null;
    stickToBottomRef.current = true;
    // DWEB-130: a new conversation — voice off, the card not yet shown, and
    // the agent's capability read afresh alongside the text session.
    explainedRef.current = false;
    typedPendingRef.current = [];
    setVoice('off');
    setVoiceNote(null);
    setVoiceDetail(null);
    setMicMuted(false);
    setVoiceCapable(false);
    readVoiceCapability().then((capable) => setVoiceCapable(capable));
    try {
      const { conversation } = await openSession(true);
      conversationRef.current = conversation;
      setPhase('live');
    } catch (e) {
      conversationRef.current = null;
      setPhase('failed');
      setDetail((prev) => prev || (e && (e.closeReason || e.message)) || null);
    }
  }, [openSession]);

  /**
   * DWEB-130: switch to voice. The voice session is opened while the text
   * one is still live (the SDK asks for the microphone first, then connects);
   * only once it has connected is the text session ended — its onDisconnect
   * is then a stale token's and ignored. On any failure the text session is
   * untouched and still active, the reason is one status line, and focus
   * returns to the composer (the core reads listening again).
   */
  const startVoice = useCallback(async () => {
    const textConv = conversationRef.current;
    if (!textConv || phase !== 'live') return;
    const prevToken = activeTokenRef.current;
    setVoice('starting');
    setVoiceNote(null);
    setVoiceDetail(null);
    setMicMuted(false);
    try {
      const { conversation } = await openSession(false);
      conversationRef.current = conversation;
      textConv.endSession().catch(() => {});
      typedPendingRef.current = [];
      liveIdRef.current = null;
      setTurn('ready');
      setVoiceMode('listening');
      setVoice('on');
    } catch (e) {
      activeTokenRef.current = prevToken;
      const kind = voiceNoteFor(e);
      setVoiceNote(VOICE_NOTES[kind]);
      setVoiceDetail(kind === 'failed' && e && e.message ? e.message : null);
      setVoice('off');
      if (inputRef.current) inputRef.current.focus();
    }
  }, [phase, openSession]);

  /** The mic: the explanation card once per conversation, then the browser's prompt. */
  const tapMic = useCallback(() => {
    if (phase !== 'live' || voice !== 'off') return;
    setVoiceNote(null);
    setVoiceDetail(null);
    if (!explainedRef.current) {
      explainedRef.current = true;
      setVoice('explaining');
      return;
    }
    startVoice();
  }, [phase, voice, startVoice]);

  const allowMic = useCallback(() => { startVoice(); }, [startVoice]);

  const keepTyping = useCallback(() => {
    setVoice('off');
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const toggleMute = useCallback(() => {
    const c = conversationRef.current;
    if (!c || voice !== 'on') return;
    const next = !micMuted;
    try {
      c.setMicMuted(next);
      setMicMuted(next);
    } catch (err) {
      setVoiceDetail(err && err.message ? err.message : String(err));
    }
  }, [voice, micMuted]);

  /**
   * DWEB-130: back to text without ending the chat — end-and-restart the
   * other way. The text session is opened first; the voice session is ended
   * once it is live. The transcript stays; the agent's conversation is new.
   */
  const endVoice = useCallback(async () => {
    const voiceConv = conversationRef.current;
    if (!voiceConv || voice !== 'on' || phase !== 'live') return;
    const prevToken = activeTokenRef.current;
    setVoice('ending');
    try {
      const { conversation } = await openSession(true);
      conversationRef.current = conversation;
      voiceConv.endSession().catch(() => {});
      typedPendingRef.current = [];
      liveIdRef.current = null;
      setTurn('ready');
      setMicMuted(false);
      setVoiceMode('listening');
      setVoice('off');
      if (inputRef.current) inputRef.current.focus();
    } catch (e) {
      activeTokenRef.current = prevToken;
      setVoice('on');
      setVoiceDetail(e && e.message ? e.message : String(e));
    }
  }, [voice, phase, openSession]);

  const end = useCallback(() => {
    const c = conversationRef.current;
    conversationRef.current = null;
    if (c) c.endSession().catch(() => {});
    setPhase('ended');
    setTurn('ready');
    setVoice('off');
    setMicMuted(false);
    liveIdRef.current = null;
    typedPendingRef.current = [];
  }, []);

  const send = useCallback((e) => {
    e.preventDefault();
    const text = draft.trim();
    const c = conversationRef.current;
    if (!text || !c || phase !== 'live') return;
    stickToBottomRef.current = true;
    append('user', text);
    setDraft('');
    setTurn('thinking');
    // DWEB-130: in a voice session the platform echoes this as a
    // user_transcript; remember it so the echo is not a second bubble.
    if (voice === 'on') typedPendingRef.current.push(text);
    try {
      c.sendUserMessage(text);
    } catch (err) {
      setDetail(err && err.message ? err.message : String(err));
    }
  }, [draft, phase, append, voice]);

  const toggle = useCallback(() => {
    if (open && !closing) close();
    else openPanel();
  }, [open, closing, close, openPanel]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') close();
  }, [close]);

  /**
   * DWEB-147: Enter submits — the form's implicit submission, which a text
   * input gets natively. Shift+Enter does NOT: the browser's implicit
   * submission ignores the modifier, so without this it submitted too
   * (measured in the gate), and a visitor holding Shift expects a line
   * break, not a send. The composer is single-line, so Shift+Enter inserts
   * nothing; it only refuses to send.
   */
  const onInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && e.shiftKey) e.preventDefault();
  }, []);

  const status = (() => {
    if (phase === 'connecting') return 'Connecting…';
    if (phase === 'failed') return FAILURE;
    if (phase === 'ended') return 'Chat ended.';
    if (phase !== 'live') return '';
    if (voice === 'starting') return 'Starting voice…';
    if (voice === 'ending') return 'Returning to text…';
    if (turn === 'thinking') return 'DynaMite is thinking…';
    if (voiceNote) return voiceNote;
    if (voice === 'on') return micMuted ? 'Microphone muted. You can type.' : 'Voice is on. Speak, or type.';
    return '';
  })();

  /** The who line: the synced head's name travels into the header (DWEB-146). */
  const who = synced ? `DynaMite (${synced.avatar})` : 'DynaMite';
  const isOpen = open && !closing;
  /**
   * DWEB-147: the core's state, derived from the text turn (see the header
   * comment). One value for the header core and the orb face; nothing is
   * scheduled to produce it.
   */
  const coreState = (() => {
    if (phase !== 'live') return 'idle';
    if (turn === 'thinking') return 'thinking';
    if (turn === 'speaking') return 'speaking';
    // DWEB-130: with voice on, the SDK's mode — speaking while the agent's
    // audio plays, otherwise listening (the mic is open), unless it is
    // muted, when the text rule applies.
    if (voice === 'on') {
      if (voiceMode === 'speaking') return 'speaking';
      if (!micMuted) return 'listening';
    }
    return isOpen && inputFocused ? 'listening' : 'idle';
  })();

  return (
    <Root data-testid="dynamite-widget" data-agent-id={DYNAMITE_AGENT_ID} data-face={face} onKeyDown={onKeyDown} $maximized={maximized}>
      {open && (
        <Panel
          ref={panelRef}
          role="dialog"
          aria-label="DynaMite chat"
          data-testid="dynamite-panel"
          data-revealed={revealed}
          $maximized={maximized}
          $revealed={revealed}
          onTransitionEnd={onPanelTransitionEnd}
        >
          <Header>
            {/* DWEB-146: the orb's core, arrived — the state indicator. `state`
                is the orb's state model, driven by the text turn since
                DWEB-147 (coreState above); see DynamiteOrb.js for why that is
                a state change and never a rAF loop. Hidden only while the
                core is in flight. */}
            <DynamiteCore state={coreState} hidden={flying} />
            <HeaderWho>
              <h2>{who}</h2>
              <small>{voiceCapable ? 'The AI Phone Receptionist, in text or voice' : 'The AI Phone Receptionist, in text'}</small>
            </HeaderWho>
            <HeaderActions>
              {phase === 'live' && (
                <QuietButton type="button" onClick={end} data-testid="dynamite-end">End chat</QuietButton>
              )}
              {/* DWEB-141: the embed's resize button. Asserted by role in
                  e2e/dynamite-panel.spec.js — remove it and the gate is red. */}
              <QuietButton
                type="button"
                onClick={() => setMaximized((v) => !v)}
                aria-label={maximized ? 'Restore DynaMite chat size' : 'Maximize DynaMite chat'}
                aria-pressed={maximized}
                data-testid="dynamite-maximize"
              >
                {maximized ? '⤡' : '⤢'}
              </QuietButton>
              <QuietButton type="button" onClick={close} aria-label="Close DynaMite chat" data-testid="dynamite-close">
                ✕
              </QuietButton>
            </HeaderActions>
          </Header>

          <Messages
            ref={listRef}
            role="log"
            aria-live="polite"
            onScroll={onListScroll}
            onPointerDown={onListPointerDown}
            onPointerUp={onListPointerUp}
            onPointerCancel={onListPointerUp}
            data-testid="dynamite-messages"
          >
            {messages.map((m) => (
              /* DWEB-147: aria-busy while a reply streams into the bubble, so a
                 screen reader announces the reply once it has settled rather
                 than every delta. */
              <Bubble key={m.id} $role={m.role} data-testid={`dynamite-message-${m.role}`} data-live={m.live ? 'true' : undefined} aria-busy={m.live || undefined}>
                {m.role === 'agent' ? (
                  <ReplyText text={m.text} />
                ) : (
                  m.text
                )}
              </Bubble>
            ))}
            {/* DWEB-130: the explanation BEFORE the browser's microphone prompt,
                once per conversation. The copy is the owner's; Allow calls
                the SDK (whose first act is getUserMedia); Keep typing puts
                focus back in the composer. */}
            {voice === 'explaining' && (
              <Notice role="note" aria-label="Before the microphone" data-testid="dynamite-mic-explainer">
                <p>{MIC_EXPLANATION}</p>
                <ExplainerActions>
                  <Primary type="button" onClick={allowMic} data-testid="dynamite-mic-allow">Allow the mic</Primary>
                  <QuietButton type="button" onClick={keepTyping} data-testid="dynamite-mic-keep-typing">Keep typing</QuietButton>
                </ExplainerActions>
              </Notice>
            )}
            {(phase === 'idle' || phase === 'ended' || phase === 'failed') && (
              <Notice data-testid="dynamite-notice">
                <p>
                  {NOTICE} The <a href="/privacy-policy">privacy policy</a> has the details.
                </p>
                <Primary ref={startRef} type="button" onClick={start} data-testid="dynamite-start">
                  {phase === 'idle' ? 'Start chat' : 'Start a new chat'}
                </Primary>
              </Notice>
            )}
          </Messages>

          <Status role="status" data-testid="dynamite-status">
            {status}
            {phase === 'failed' && detail && <small data-testid="dynamite-error-detail">{detail}</small>}
            {phase === 'live' && voiceDetail && <small data-testid="dynamite-voice-detail">{voiceDetail}</small>}
            {phase === 'live' && conversationId && (
              <small data-testid="dynamite-conversation-id">Conversation {conversationId}</small>
            )}
          </Status>

          <Composer onSubmit={send}>
            {/* DWEB-130 (v6.6.3): the mic — only while the session is live AND
                the agent said it can listen (voiceCapable, read at Start).
                With voice on it becomes Mute, beside End voice. */}
            {phase === 'live' && voiceCapable && voice !== 'on' && voice !== 'ending' && (
              <MicButton
                type="button"
                onClick={tapMic}
                disabled={voice !== 'off'}
                aria-label="Speak to DynaMite"
                data-testid="dynamite-mic"
                data-voice={voice}
              >
                <MicIcon />
              </MicButton>
            )}
            {phase === 'live' && (voice === 'on' || voice === 'ending') && (
              <>
                <MicButton
                  type="button"
                  onClick={toggleMute}
                  disabled={voice === 'ending'}
                  aria-pressed={micMuted}
                  aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
                  data-testid="dynamite-mute"
                  $live={!micMuted}
                >
                  <MicIcon muted={micMuted} />
                </MicButton>
                <QuietButton type="button" onClick={endVoice} disabled={voice === 'ending'} aria-label="End voice and keep typing" data-testid="dynamite-voice-end">
                  End voice
                </QuietButton>
              </>
            )}
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onInputKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={phase !== 'live' ? 'Start the chat to ask a question' : voice === 'on' ? 'Speak, or type…' : PLACEHOLDER}
              disabled={phase !== 'live'}
              aria-label="Message DynaMite"
              data-testid="dynamite-input"
            />
            <Send type="submit" disabled={phase !== 'live' || !draft.trim()} aria-label="Send" data-testid="dynamite-send">
              ➤
            </Send>
          </Composer>
        </Panel>
      )}

      {/* DWEB-146: the travelling core, orb face only, from the click until it arrives. */}
      {flying && flyStartRef.current && (
        <Fly
          ref={flyRef}
          aria-hidden="true"
          data-testid="dynamite-fly"
          style={{ '--x': `${flyStartRef.current.x}px`, '--y': `${flyStartRef.current.y}px` }}
          onTransitionEnd={onFlyTransitionEnd}
        />
      )}

      {/* THE LAUNCHER'S FACE (DWEB-146): the synced GLB head while the demo's
          sync toggle is on, otherwise the default face — the orb, or the
          avatar canvas when the config (or the suite's override) says so.
          With the avatar (DWEB-131) the launcher is the canvas; its badge
          ("DynaMite" / "DynaMite (regina)") is what the sync scenarios read.
          It gets `isOpen` for its name and aria-expanded ONLY: since
          DWEB-148 (v6.6.2) neither the open panel nor its maximized state is
          an input to the head's framing — the launcher is a constant circle
          whatever the panel does, and the coupling that shrank the head
          when the chat opened is gone from avatarConfig.js. With the orb no
          canvas mounts and no three.js chunk is fetched until a sync
          promotes the head in. */}
      <LauncherRow>
        {/* DWEB-142: the bubble, beside the launcher and never instead of it.
            Asserted by role in e2e/dynamite-bubble.spec.js — remove it and
            the gate is red; render it before consent and the gate is red. */}
        {consentDecided && !bubbleDismissed && !open && !openedOnce && (
          <Callouts>
            <Callout role="note" aria-label="DynaMite suggestion" data-testid="dynamite-bubble">
              <CalloutLines>
                <CalloutLine type="button" onClick={openFromBubble} data-testid="dynamite-bubble-open">
                  {BUBBLE_COPY}
                </CalloutLine>
                {/* DWEB-140: the call route, in the same bubble — never a second one. */}
                <CalloutCall data-testid="dynamite-bubble-call">
                  {'\u2026or call '}
                  <a href={CONTACT_PHONE.href} aria-label={CONTACT_PHONE.label}>{CONTACT_PHONE.display}</a>
                </CalloutCall>
              </CalloutLines>
              <CalloutDismiss
                type="button"
                onClick={dismissBubble}
                aria-label="Dismiss the DynaMite suggestion"
                data-testid="dynamite-bubble-dismiss"
              >
                ✕
              </CalloutDismiss>
            </Callout>
            {/* DWEB-140 (v6.6.4): the call route is the SECOND LINE of the bubble
                above, not a second bubble — one dismissal, one key. */}
          </Callouts>
        )}
        {face === 'avatar' ? (
          <Suspense
            fallback={(
              <LauncherFallback
                type="button"
                onClick={toggle}
                aria-label={isOpen ? 'Close DynaMite chat' : 'Open DynaMite chat'}
                aria-expanded={isOpen}
                data-testid="dynamite-launcher"
                data-launcher="avatar"
              >
                <img src="/images/logos/dynamite.webp" alt="" width="72" height="72" />
              </LauncherFallback>
            )}
          >
            <DynamiteAvatar onClick={toggle} isOpen={isOpen} />
          </Suspense>
        ) : (
          /* DWEB-144 / DWEB-146 / DWEB-147: the orb. `state` mirrors the
             header core's — a turn still in flight shows on the launcher when
             the panel is closed — and runs no keyframe on the face itself;
             the pull lives inside. DWEB-150: its core layer is hidden while
             the panel is mounted and until the return flight lands; the
             sphere stays. */
          <DynamiteOrb ref={orbRef} onClick={toggle} isOpen={isOpen} state={coreState} coreHidden={open || flying} />
        )}
      </LauncherRow>
    </Root>
  );
}
